import Button from '@/components/Button';
import Typography from '@/components/Typography';
import { MasterTranslation, TextStyles } from '@/constants';
import { useGlobalState, useResponsive } from '@/hooks';
import { ButtonColorKey, ButtonVariant, ResponsiveCollection } from '@/interfaces';
import type { AffiliateProduct } from '@/modules/pmt/interfaces';
import { mapBadgeType } from '@/modules/pmt/utils/affiliate.utils';
import { formatPrice } from '@/modules/pmt/utils/price.utils';
import type { FC } from 'react';
import ProductBadge from '../ProductBadge';
import {
  BadgeContainer,
  Container,
  CostContainer,
  CurrentPrice,
  OriginalPrice,
  PriceContainer,
  ProductImage,
  Title,
  Wrapper,
} from './ProductCard.styles';

interface ProductCardProps {
  product: AffiliateProduct;
  onClick?: () => void;
}

const ProductCard: FC<ProductCardProps> = ({ product, onClick }) => {
  const { id, title, imageUrls, originalPrice, afterDiscountPrice, badge, affiliateUrl } = product;
  const { isMobile } = useResponsive([ResponsiveCollection.Mobile]);
  const { t } = useGlobalState();

  const formattedPrice =
    afterDiscountPrice?.amount != null && formatPrice(afterDiscountPrice.amount, afterDiscountPrice.currency);
  const formattedOriginalPrice =
    originalPrice?.amount != null && formatPrice(originalPrice.amount, originalPrice.currency);
  const badgeType = mapBadgeType(badge);
  const hasDiscount =
    !!originalPrice?.amount &&
    !!afterDiscountPrice?.amount &&
    originalPrice?.amount > 0 && // Ensure original price is greater than 0
    originalPrice.amount !== afterDiscountPrice.amount;

  return (
    <Container key={id}>
      <Wrapper>
        <ProductImage src={imageUrls.large} alt={title} width={isMobile ? 80 : 172} height={isMobile ? 80 : 172} />

        <PriceContainer>
          <Title>
            <Typography variant={TextStyles['Heading 5']}>{title}</Typography>
          </Title>

          {/* Product Badge */}
          {badgeType && (
            <BadgeContainer>
              <ProductBadge type={badgeType} />
            </BadgeContainer>
          )}

          {/* Product Price */}
          <CostContainer>
            <CurrentPrice>
              <Typography variant={TextStyles['Heading 2']}>{formattedPrice}</Typography>
            </CurrentPrice>
            {hasDiscount && <OriginalPrice>{formattedOriginalPrice}</OriginalPrice>}
          </CostContainer>

          <Button
            variant={ButtonVariant.Outlined}
            color={ButtonColorKey.Blue}
            href={affiliateUrl}
            onClick={onClick}
            openInNewTab={true}
          >
            {t(MasterTranslation.AffiliateSectionCta)}
          </Button>
        </PriceContainer>
      </Wrapper>
    </Container>
  );
};

export default ProductCard;
